// src/components/users/UserForm.js
import { useState, useEffect } from 'react';
import {
  fetchAllProjects,
  saveUser,
  createUser,
  getInitialFormData,
  validateEmail,
} from '../../services/userFormService';
import useAuth from '../../hooks/useAuth';
import { getAllRoles } from '../../services/roleService';

const UserForm = ({ user, onClose, onUpdated }) => {
  const { user: currentUser } = useAuth();
  const roleNames = new Set(currentUser?.roles?.map(r => r.name) || []);

  const [formData, setFormData] = useState(() => {
    const data = getInitialFormData(user);
    return {
      ...data
    };
  });
  const [allProjects, setAllProjects] = useState([]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setPasswordConfirm] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyticsView, setAnalyticsView] = useState(false);

  const isEditingSelf = user?.id === currentUser?.id;
  const isSuperuser = currentUser?.superuser;
  const isAdmin = roleNames.has('admin');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState(user?.roles?.map(r => r.id) || []);

  // Загружаем доступные роли с бэкенда
  useEffect(() => {
    const loadAvailableRoles = async () => {
      const roles = await getAllRoles();
      setAvailableRoles(roles);
      
      // Если редактируем пользователя, устанавливаем его текущие роли
      if (user?.roles) {
        const roleIds = user.roles.map(r => r.id);
        setSelectedRoles(roleIds);
      }
    };
    
    loadAvailableRoles();
  }, [user]);

  // console.log('user', user)
  // console.log('canManageRoles', canManageRoles)
  // console.log('currentUser', currentUser)
  // Загрузка проектов
  useEffect(() => {
    fetchAllProjects().then(setAllProjects);
  }, []);

  // Сброс формы при смене пользователя
  useEffect(() => {
    setFormData(getInitialFormData(user));
    setPassword('');
    setPasswordConfirm('');
    setOldPassword('');
    setError('');
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    
    // Обработка специального чекбокса analytics_view
    if (name === 'analytics_view') {
      setAnalyticsView(checked);
    }
  };

  const handleRolesChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => Number(opt.value));
    setSelectedRoles(selected);
  };

  const handleProjectChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => Number(opt.value));
    setFormData((prev) => ({ ...prev, projects: selected }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // === Создание нового пользователя ===
    if (!user?.id) {
      const result = await createUser(formData, password, confirmPassword, selectedRoles);
      if (result.success) {
        onUpdated(result.user);
      } else {
        setError(result.error);
      }
    }
    // === Редактирование существующего ===
    else {
      const result = await saveUser(
        user,
        formData,
        password,
        confirmPassword,
        oldPassword,
        isEditingSelf,
        isAdmin,
        isSuperuser,
        selectedRoles
      );
      if (result.success) {
        onUpdated();
      } else {
        setError(result.error);
      }
    }

    setLoading(false);
  };

  // Функция для безопасного закрытия формы
  const handleClose = () => {
    setOldPassword('');
    setPassword('');
    setPasswordConfirm('');
    setError('');
    onClose();
  };

  // Останавливаем всплытие клика внутри модального окна
  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="user-form-modal"
    >
      <div
        className="user-form-dialog"
        onClick={handleDialogClick} // Останавливаем закрытие при клике внутри
      >
        <div className="user-form-content">
          <div className="user-form-header">
            <h5 className="modal-title">
              {user?.id ? 'Редактировать' : 'Новый'} пользователь
            </h5>
            <button className="text-modal-close" onClick={handleClose}>
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="user-form-body">
              {error && <div className="alert alert-danger">{error}</div>}

              <div className="mb-3">
                <label>Логин *</label>
                <input
                  type="text"
                  className="form-control"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label>Имя</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label>Фамилия</label>
                <input
                  type="text"
                  className="form-control"
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label>ФИО в эра</label>
                <input
                  type="text"
                  className="form-control"
                  name="era_name"
                  value={formData.era_name || ''}
                  onChange={handleChange}
                  placeholder="Фамилия И. О."
                />
              </div>

              <div className="mb-3">
                <label>Email</label>
                <input
                  type="email"
                  className={`form-control ${formData.email && !validateEmail(formData.email) ? 'is-invalid' : ''}`}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@example.com"
                />
                {formData.email && !validateEmail(formData.email) && (
                  <div className="invalid-feedback d-block">
                    Введите корректный email
                  </div>
                )}
              </div>

              {/* Поля admin и analytics_view убраны из формы, так как управляются через роли */}
              
              {availableRoles.length > 0 && (
                <div className="mb-3">
                  <label>Роли</label>
                  <select
                    multiple
                    className="form-control"
                    value={selectedRoles}
                    onChange={handleRolesChange}
                  >
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-3">
                <label>Проекты</label>
                <select
                  multiple
                  className="form-control"
                  value={formData.projects}
                  onChange={handleProjectChange}
                >
                  {allProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Смена пароля */}
              {user?.id && (
                <div className="mb-3 border-top pt-3">
                  <h6>Смена пароля</h6>

                  {isAdmin && isEditingSelf && (
                    <div className="mb-3">
                      <label>Старый пароль</label>
                      <input
                        type="password"
                        className="form-control"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="mb-3">
                    <label>Новый пароль</label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Оставьте пустым, если не меняете"
                    />
                  </div>
                  <div className="mb-3">
                    <label>Подтвердите новый пароль</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Новый пользователь — ввод пароля обязателен */}
              {!user?.id && (
                <>
                  <div className="mb-3">
                    <label>Пароль *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>Подтвердите пароль *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      required
                    />
                  </div>
                </>
              )}
            </div>
            <div className="user-form-footer">
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                Отмена
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;




